import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import type { InventoryItem } from '../types/inventory.types';
import i18n from '../../../../i18n';

/**
 * Excel export/import işlemleri için service
 */
export class ExcelService {
  /**
   * Envanter ürünlerini Excel'e aktar
   */
  static async exportToExcel(
    items: InventoryItem[],
    getCategoryName: (categoryId: string | null) => string,
    getBrandName: (brandId: string | null) => string,
    getWarehouseName: (warehouseId: string | null) => string,
    getBranchName: (branchId: string | null) => string,
    getCurrencySymbol: (currency: string) => string,
    includeProduction = false
  ): Promise<void> {
    const isEn = i18n.language?.toLowerCase().startsWith('en');
    const locale = isEn ? 'en-US' : 'tr-TR';
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(isEn ? 'Stock List' : 'Stok Listesi');

    // Sütun başlıkları
    worksheet.columns = [
      { header: isEn ? 'Product Name' : 'Ürün Adı', key: 'name', width: 30 },
      { header: isEn ? 'Barcode' : 'Barkod', key: 'barcode', width: 20 },
      { header: isEn ? 'Stock Code' : 'Stok Kodu', key: 'stockCode', width: 20 },
      { header: isEn ? 'Category' : 'Kategori', key: 'category', width: 25 },
      { header: isEn ? 'Brand' : 'Marka', key: 'brand', width: 20 },
      { header: isEn ? 'Branch' : 'Şube', key: 'branch', width: 20 },
      { header: isEn ? 'Quantity' : 'Miktar', key: 'quantity', width: 15 },
      { header: isEn ? 'Unit' : 'Birim', key: 'unit', width: 15 },
      { header: isEn ? 'Purchase Price' : 'Alış Fiyatı', key: 'purchasePrice', width: 15 },
      { header: isEn ? 'Purchase Currency' : 'Alış Para Birimi', key: 'purchaseCurrency', width: 15 },
      { header: isEn ? 'Sale Price' : 'Satış Fiyatı', key: 'salePrice', width: 15 },
      { header: isEn ? 'Sale Currency' : 'Satış Para Birimi', key: 'saleCurrency', width: 15 },
      { header: isEn ? 'VAT Rate (%)' : 'KDV Oranı (%)', key: 'vatRate', width: 15 },
      { header: isEn ? 'VAT Status' : 'KDV Durumu', key: 'vatStatus', width: 15 },
      { header: isEn ? 'SCT Rate (%)' : 'ÖTV Oranı (%)', key: 'sctRate', width: 15 },
      { header: isEn ? 'SCT Status' : 'ÖTV Durumu', key: 'sctStatus', width: 15 },
      { header: isEn ? 'Warehouse' : 'Depo', key: 'warehouse', width: 20 },
      { header: isEn ? 'Description' : 'Açıklama', key: 'description', width: 40 },
      { header: isEn ? 'Image URL' : 'Resim URL', key: 'imageUrl', width: 60 },
      { header: isEn ? 'Status' : 'Durum', key: 'isActive', width: 15 },
      { header: isEn ? 'E-Commerce' : 'E-Ticaret', key: 'isEcommerce', width: 15 },
      { header: isEn ? 'QR Menu' : 'QR Menü', key: 'isQRMenu', width: 15 },
      { header: isEn ? 'Created At' : 'Oluşturma Tarihi', key: 'createdAt', width: 20 },
      ...(includeProduction ? [
        { header: i18n.t('stockCard.itemClass', { ns: 'production' }), key: 'itemClass', width: 18 },
        { header: i18n.t('stockCard.productionMode', { ns: 'production' }), key: 'productionMode', width: 22 },
      ] : []),
    ];

    // Başlık satırını stillendir
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1976D2' }
    };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
    headerRow.height = 25;

    // Veri satırları
    items.forEach((item) => {
      worksheet.addRow({
        name: item.name,
        barcode: item.barcode || '',
        stockCode: item.stockCode || '',
        category: getCategoryName(item.categoryId || null),
        brand: getBrandName(item.brandId || null),
        branch: getBranchName(item.branchesId || null),
        // ✅ YENİ SİSTEM: WarehouseStocks'tan toplam hesapla
        quantity: item.warehouseStocks?.reduce((sum, ws) => sum + ws.quantity, 0) || 0,
        unit: item.unit,
        purchasePrice: item.purchasePrice,
        purchaseCurrency: getCurrencySymbol(item.purchaseCurrency || 'TRY'),
        salePrice: item.salePrice,
        saleCurrency: getCurrencySymbol(item.saleCurrency || 'TRY'),
        vatRate: item.vatRate || 0,
        vatStatus: item.vatStatus === 'included' ? (isEn ? 'Included' : 'Dahil') : (isEn ? 'Excluded' : 'Hariç'),
        sctRate: item.sctRate || 0,
        sctStatus: item.sctStatus === 'included' ? (isEn ? 'Included' : 'Dahil') : (isEn ? 'Excluded' : 'Hariç'),
        warehouse: item.warehouseStocks && item.warehouseStocks.length > 0
          ? item.warehouseStocks.map(ws => ws.warehouseName || ws.warehouseId).join(', ')
          : '-',
        description: item.description || '',
        imageUrl: item.imageUrl || '',
        isActive: item.isActive ? (isEn ? 'Active' : 'Aktif') : (isEn ? 'Inactive' : 'Pasif'),
        isEcommerce: item.isEcommerce ? (isEn ? 'Yes' : 'Evet') : (isEn ? 'No' : 'Hayır'),
        isQRMenu: item.isQRMenu ? (isEn ? 'Yes' : 'Evet') : (isEn ? 'No' : 'Hayır'),
        createdAt: item.createdAt ? new Date(item.createdAt).toLocaleDateString(locale) : '',
        itemClass: item.itemClass
          ? i18n.t(`stockCard.classes.${item.itemClass}`, { ns: 'production', defaultValue: item.itemClass })
          : '',
        productionMode: item.productionMode === 'MAKE_TO_STOCK'
          ? i18n.t('stockCard.list.stocked', { ns: 'production' })
          : i18n.t('stockCard.list.onSale', { ns: 'production' }),
      });
    });

    // Alternatif satır renklendirme
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) { // Başlık satırını atla
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFD3D3D3' } },
            left: { style: 'thin', color: { argb: 'FFD3D3D3' } },
            bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } },
            right: { style: 'thin', color: { argb: 'FFD3D3D3' } }
          };
          
          if (rowNumber % 2 === 0) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFF5F5F5' }
            };
          }
        });
      }
    });

    // Sayı sütunlarını formatla
    worksheet.getColumn('quantity').numFmt = '#,##0';
    worksheet.getColumn('purchasePrice').numFmt = '#,##0.00';
    worksheet.getColumn('salePrice').numFmt = '#,##0.00';
    worksheet.getColumn('vatRate').numFmt = '0';
    worksheet.getColumn('sctRate').numFmt = '0';

    // Excel dosyasını oluştur ve indir
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const fileName = `${isEn ? 'stock-list' : 'stok-listesi'}-${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(blob, fileName);
  }

  /**
   * Excel şablonu indir (import için)
   */
  static async downloadTemplate(): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Stok Şablonu');

    worksheet.columns = [
      { header: 'Ürün Adı*', key: 'name', width: 30 },
      { header: 'Barkod', key: 'barcode', width: 20 },
      { header: 'Stok Kodu*', key: 'stockCode', width: 20 },
      { header: 'Kategori ID', key: 'categoryId', width: 25 },
      { header: 'Marka ID', key: 'brandId', width: 20 },
      { header: 'Miktar*', key: 'quantity', width: 15 },
      { header: 'Birim*', key: 'unit', width: 15 },
      { header: 'Alış Fiyatı', key: 'purchasePrice', width: 15 },
      { header: 'Satış Fiyatı*', key: 'salePrice', width: 15 },
      { header: 'KDV Oranı', key: 'vatRate', width: 15 },
      { header: 'Açıklama', key: 'description', width: 40 },
    ];

    // Başlık satırını stillendir
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1976D2' }
    };

    // Örnek satır ekle
    worksheet.addRow({
      name: 'Örnek Ürün',
      barcode: '1234567890123',
      stockCode: 'ORN-001',
      categoryId: '',
      brandId: '',
      quantity: 10,
      unit: 'adet',
      purchasePrice: 50,
      salePrice: 100,
      vatRate: 18,
      description: 'Örnek ürün açıklaması',
    });

    // Açıklama sayfası ekle
    const infoSheet = workbook.addWorksheet('Bilgilendirme');
    infoSheet.getColumn(1).width = 80;
    
    infoSheet.addRow(['STOK İÇE AKTARMA ŞABLONU']);
    infoSheet.addRow(['']);
    infoSheet.addRow(['* işareti ile belirtilen alanlar zorunludur.']);
    infoSheet.addRow(['']);
    infoSheet.addRow(['Alan Açıklamaları:']);
    infoSheet.addRow(['- Ürün Adı: Ürünün tam adı (Zorunlu)']);
    infoSheet.addRow(['- Barkod: Ürün barkod numarası']);
    infoSheet.addRow(['- Stok Kodu: Benzersiz stok kodu (Zorunlu)']);
    infoSheet.addRow(['- Kategori ID: Kategori ID numarası']);
    infoSheet.addRow(['- Marka ID: Marka ID numarası']);
    infoSheet.addRow(['- Miktar: Ürün miktarı (Zorunlu)']);
    infoSheet.addRow(['- Birim: Ölçü birimi (adet, kg, lt vb.) (Zorunlu)']);
    infoSheet.addRow(['- Alış Fiyatı: Ürün alış fiyatı']);
    infoSheet.addRow(['- Satış Fiyatı: Ürün satış fiyatı (Zorunlu)']);
    infoSheet.addRow(['- KDV Oranı: KDV oranı (%)']);
    infoSheet.addRow(['- Açıklama: Ürün açıklaması']);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    saveAs(blob, 'stok-import-sablonu.xlsx');
  }
}

